import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Eye, RefreshCw } from 'lucide-react';
import { useAdminHubStore } from '../stores/adminHubStore';
import { useHubStore } from '../stores/hubStore';
import { useQuoteStore } from '../stores/quoteStore';
import { AdminShadowJob, useAdminStore } from '../stores/adminStore';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { RecruiterHubView } from '../components/hub/RecruiterHubView';
import { FreelancerHubView } from '../components/hub/FreelancerHubView';
import AdminJobDetailsModal from '../components/admin/modals/AdminJobDetailsModal';
import AdminEditJobModal from '../components/admin/modals/AdminEditJobModal';
import AdminUnpublishJobModal from '../components/admin/modals/AdminUnpublishJobModal';
import AdminPublishJobModal from '../components/admin/modals/AdminPublishJobModal';
import AdminAssignJobToRecruiterModal from '../components/admin/modals/AdminAssignJobToRecruiterModal';
import UpdateJobStatusModal from '../components/jobs/UpdateJobStatusModal';
import ShadowRecruiterManagerModal from '../components/admin/ShadowRecruiterManagerModal';
import type { HubContext } from '../types/hub';

type AdminPerspective = 'recruiter' | 'freelancer';

export default function AdminJobHubPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedQuoteId = searchParams.get('selectedQuoteId');

  const perspectiveParam = searchParams.get('adminPerspective');
  const perspective: AdminPerspective =
    perspectiveParam === 'freelancer' ? 'freelancer' : 'recruiter';
  const adminPrincipalQuoteId = searchParams.get('adminPrincipalQuoteId');
  const adminEffectiveViewerIdParam = searchParams.get('adminEffectiveViewerId');

  const setSelectedQuoteId = useCallback(
    (quoteId: string | null) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (quoteId) {
            next.set('selectedQuoteId', quoteId);
          } else {
            next.delete('selectedQuoteId');
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPerspective = useCallback(
    (next: AdminPerspective) => {
      setSearchParams(
        prev => {
          const n = new URLSearchParams(prev);
          n.set('adminPerspective', next);
          if (next === 'recruiter') {
            n.delete('adminPrincipalQuoteId');
          }
          return n;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setAdminPrincipalQuoteId = useCallback(
    (id: string | null) => {
      setSearchParams(
        prev => {
          const n = new URLSearchParams(prev);
          if (id) {
            n.set('adminPrincipalQuoteId', id);
          } else {
            n.delete('adminPrincipalQuoteId');
          }
          return n;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();

  const {
    context,
    contextLoading,
    contextError,
    fetchHubContext,
    clearHubContext,
    resetInteractions,
  } = useHubStore();

  const { quotes: quoteStoreQuotes, fetchQuoteById } = useQuoteStore();

  const {
    unreadByParticipant,
    loadingUnread,
    error,
    fetchAdminUnreadState,
    reset: resetAdmin,
  } = useAdminHubStore();

  const [recruiterQuotes, setRecruiterQuotes] = useState<HubContext['quotes']>([]);

  useEffect(() => {
    if (!jobId) return;
    void fetchAdminUnreadState(jobId);
  }, [jobId, fetchAdminUnreadState]);

  useEffect(() => {
    return () => {
      clearHubContext();
      resetInteractions();
      resetAdmin();
    };
  }, [clearHubContext, resetInteractions, resetAdmin]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    void (async () => {
      const ctxRec = await fetchHubContext(jobId, { adminEffectiveViewerId: null });
      if (cancelled) return;
      if (ctxRec?.quotes?.length) {
        setRecruiterQuotes(ctxRec.quotes);
      } else {
        setRecruiterQuotes([]);
      }
      const eff =
        perspective === 'freelancer'
          ? (adminEffectiveViewerIdParam ??
            (ctxRec?.quotes?.find(q => q.id === adminPrincipalQuoteId) ?? ctxRec?.quotes?.[0])
              ?.freelancer_id ??
            null)
          : null;
      if (perspective === 'freelancer' && eff) {
        await fetchHubContext(jobId, { adminEffectiveViewerId: eff });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, perspective, adminPrincipalQuoteId, adminEffectiveViewerIdParam, fetchHubContext]);

  useEffect(() => {
    if (perspective !== 'freelancer' || recruiterQuotes.length === 0) return;
    if (!adminPrincipalQuoteId) {
      setAdminPrincipalQuoteId(recruiterQuotes[0].id);
    }
  }, [perspective, recruiterQuotes, adminPrincipalQuoteId, setAdminPrincipalQuoteId]);

  const adminEffectiveViewerId = useMemo(() => {
    if (perspective !== 'freelancer') return null;
    if (adminEffectiveViewerIdParam) return adminEffectiveViewerIdParam;
    const row = recruiterQuotes.find(q => q.id === adminPrincipalQuoteId) ?? recruiterQuotes[0];
    return row?.freelancer_id ?? null;
  }, [perspective, recruiterQuotes, adminPrincipalQuoteId, adminEffectiveViewerIdParam]);

  const principalQuoteIdForFreelancerView =
    context?.principal_quote_id ?? adminPrincipalQuoteId ?? null;

  useEffect(() => {
    if (!principalQuoteIdForFreelancerView || perspective !== 'freelancer') return;
    void fetchQuoteById(principalQuoteIdForFreelancerView);
  }, [principalQuoteIdForFreelancerView, perspective, fetchQuoteById]);

  const hubQuote =
    principalQuoteIdForFreelancerView != null
      ? (quoteStoreQuotes.find(q => String(q.id) === String(principalQuoteIdForFreelancerView)) ??
        null)
      : null;

  const reloadHub = useCallback(async () => {
    if (!jobId) return;
    const ctxRec = await fetchHubContext(jobId, { adminEffectiveViewerId: null });
    if (ctxRec?.quotes?.length) {
      setRecruiterQuotes(ctxRec.quotes);
    }
    if (perspective === 'freelancer') {
      const eff =
        adminEffectiveViewerIdParam ??
        (ctxRec?.quotes?.find(q => q.id === adminPrincipalQuoteId) ?? ctxRec?.quotes?.[0])
          ?.freelancer_id ??
        null;
      if (eff) {
        await fetchHubContext(jobId, { adminEffectiveViewerId: eff });
      }
    }
    await fetchAdminUnreadState(jobId);
  }, [
    jobId,
    perspective,
    adminPrincipalQuoteId,
    adminEffectiveViewerIdParam,
    fetchHubContext,
    fetchAdminUnreadState,
  ]);

  const { fetchJobById } = useAdminStore();

  const [detailsJob, setDetailsJob] = useState<AdminShadowJob | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showAssignRecruiterModal, setShowAssignRecruiterModal] = useState(false);
  const [showShadowRecruiterManager, setShowShadowRecruiterManager] = useState(false);

  const openJobDetails = useCallback(async () => {
    if (!jobId) return;
    setLoadingDetails(true);
    try {
      const job = await fetchJobById(jobId);
      if (job) {
        setDetailsJob(job);
        setShowDetailsModal(true);
      } else {
        toast.error(t('jobActivityHub.details_load_error', { ns: 'admin' }));
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error(t('jobActivityHub.details_load_error', { ns: 'admin' }));
    } finally {
      setLoadingDetails(false);
    }
  }, [jobId, fetchJobById, t]);

  const refreshJobDetails = useCallback(async () => {
    if (!jobId) return;
    try {
      const job = await fetchJobById(jobId);
      if (job) {
        setDetailsJob(job);
      }
    } catch (error) {
      console.error('Error refreshing job details:', error);
    }
  }, [jobId, fetchJobById]);

  if (!jobId) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/[0.08] dark:bg-white/[0.03]">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('jobActivityHub.invalid_job', { ns: 'admin' })}
        </p>
        <Link to="/admin/jobs" className="mt-4 inline-block text-primary underline">
          {t('navigation.jobs', { ns: 'admin' })}
        </Link>
      </div>
    );
  }

  const jobTitle = context?.job?.title || t('jobActivityHub.fallback_title', { ns: 'admin' });
  const jobStatus = context?.job?.status ?? '';

  const hasQuotes =
    (context?.job?.quotes?.length ?? 0) > 0 || !!(context?.principal_quote_id ?? null);
  const shouldShowCommunicationHub = context?.job?.visibility === 'private' || hasQuotes;

  const isRecruiterHubContext =
    context?.view_type === 'recruiter_multi' || context?.view_type === 'recruiter_project';
  const isFreelancerHubContext =
    context?.view_type === 'freelancer' || context?.view_type === 'alliance';

  const hubError = contextError || error;

  const showFreelancerUi =
    perspective === 'freelancer' ? isFreelancerHubContext && adminEffectiveViewerId : false;
  const showRecruiterUi = perspective === 'recruiter' ? isRecruiterHubContext : false;

  return (
    <div className="flex w-full flex-col gap-6 pb-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/admin/jobs')}
            className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            <ArrowLeft size={16} />
            {t('jobActivityHub.back_to_jobs', { ns: 'admin' })}
          </button>
          <h1 className="text-3xl font-bold hyphens-auto md:text-4xl">
            {t('jobActivityHub.title', { ns: 'admin' })}
          </h1>
          <p className="mt-1 text-lg text-gray-700 dark:text-gray-300">{jobTitle}</p>
          {jobStatus ? (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('table_fields.status', { ns: 'admin' })}: {jobStatus}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <Button
            type="button"
            variant="alternate"
            disabled={loadingDetails}
            onClick={() => void openJobDetails()}
          >
            <Eye size={16} className="mr-2 inline" />
            {loadingDetails
              ? t('loading', { ns: 'common' })
              : t('jobActivityHub.view_details', { ns: 'admin' })}
          </Button>
          <Button
            type="button"
            variant="alternate"
            disabled={contextLoading || loadingUnread}
            onClick={() => void reloadHub()}
          >
            <RefreshCw size={16} className="mr-2 inline" />
            {t('jobActivityHub.refresh', { ns: 'admin' })}
          </Button>
        </div>
      </div>

      {hubError ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200"
          role="alert"
        >
          {hubError}
        </div>
      ) : null}

      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] md:p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('jobActivityHub.unread_section', { ns: 'admin' })}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('jobActivityHub.unread_help', { ns: 'admin' })}
        </p>
        {loadingUnread ? (
          <p className="mt-4 text-sm text-gray-500">{t('loading', { ns: 'common' })}</p>
        ) : unreadByParticipant.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">
            {t('jobActivityHub.no_participants', { ns: 'admin' })}
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-gray-100 dark:divide-white/[0.06]">
            {unreadByParticipant.map(row => (
              <li
                key={row.profile_id}
                className="flex flex-wrap items-center justify-between gap-2 py-3"
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {[row.first_name, row.last_name].filter(Boolean).join(' ') || row.profile_id}
                </span>
                <span
                  className={
                    row.unread_notification_count > 0
                      ? 'rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
                      : 'rounded-full bg-green-100 px-3 py-0.5 text-xs font-semibold text-green-900 dark:bg-green-900/40 dark:text-green-100'
                  }
                >
                  {row.unread_notification_count > 0
                    ? t('jobActivityHub.unread_count', {
                        ns: 'admin',
                        count: row.unread_notification_count,
                      })
                    : t('jobActivityHub.all_seen', { ns: 'admin' })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] md:p-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('jobActivityHub.perspective_label', { ns: 'admin' })}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('jobActivityHub.perspective_help', { ns: 'admin' })}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-white/[0.08]">
            <button
              type="button"
              onClick={() => setPerspective('recruiter')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                perspective === 'recruiter'
                  ? 'bg-primary text-white'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.06]'
              }`}
            >
              {t('jobActivityHub.perspective_recruiter', { ns: 'admin' })}
            </button>
            <button
              type="button"
              onClick={() => setPerspective('freelancer')}
              disabled={recruiterQuotes.length === 0}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                perspective === 'freelancer'
                  ? 'bg-primary text-white'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.06]'
              }`}
            >
              {t('jobActivityHub.perspective_freelancer', { ns: 'admin' })}
            </button>
          </div>
          {perspective === 'freelancer' && recruiterQuotes.length > 0 ? (
            <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span>{t('jobActivityHub.principal_offer_label', { ns: 'admin' })}</span>
              <select
                className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-white/[0.12] dark:bg-white/[0.04]"
                value={adminPrincipalQuoteId ?? recruiterQuotes[0]?.id ?? ''}
                onChange={e => setAdminPrincipalQuoteId(e.target.value || null)}
              >
                {recruiterQuotes.map(q => (
                  <option key={q.id} value={q.id}>
                    {[q.quote_owner_first_name, q.quote_owner_last_name]
                      .filter(Boolean)
                      .join(' ') || q.id}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </section>

      {contextLoading ? (
        <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-gray-200 bg-white dark:border-white/[0.08] dark:bg-white/[0.03]">
          <LoadingSpinner />
        </div>
      ) : !context ? (
        <p className="text-sm text-gray-500">{t('loading', { ns: 'common' })}</p>
      ) : !shouldShowCommunicationHub ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('jobActivityHub.no_hub_threads', { ns: 'admin' })}
        </p>
      ) : showRecruiterUi ? (
        <RecruiterHubView
          context={context}
          selectedQuoteId={selectedQuoteId}
          onSelectedQuoteIdChange={setSelectedQuoteId}
          readOnly
          adminEffectiveViewerId={null}
        />
      ) : showFreelancerUi && principalQuoteIdForFreelancerView && hubQuote != null ? (
        <FreelancerHubView
          context={context}
          quote={hubQuote}
          quoteId={principalQuoteIdForFreelancerView}
          selectedQuoteId={selectedQuoteId}
          onSelectedQuoteIdChange={setSelectedQuoteId}
          readOnly
          adminEffectiveViewerId={adminEffectiveViewerId}
        />
      ) : showFreelancerUi && principalQuoteIdForFreelancerView && hubQuote == null ? (
        <p className="text-sm text-gray-500">{t('loading', { ns: 'common' })}</p>
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('jobActivityHub.no_hub_threads', { ns: 'admin' })}
        </p>
      )}

      {showDetailsModal && detailsJob && (
        <AdminJobDetailsModal
          job={detailsJob}
          showActivityHubLink={false}
          onClose={() => setShowDetailsModal(false)}
          onClick={() => setShowUpdateStatusModal(true)}
          onEdit={() => setShowEditModal(true)}
          onUnpublish={() => setShowUnpublishModal(true)}
          onPublish={() => setShowPublishModal(true)}
          onAssignToRecruiter={() => setShowAssignRecruiterModal(true)}
          onManageShadowRecruiter={() => setShowShadowRecruiterManager(true)}
          onJobFieldUpdated={patch => setDetailsJob(prev => (prev ? { ...prev, ...patch } : prev))}
        />
      )}

      {showEditModal && detailsJob && (
        <AdminEditJobModal
          job={detailsJob}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            setShowDetailsModal(false);
            void reloadHub();
          }}
        />
      )}

      {showUnpublishModal && detailsJob && (
        <AdminUnpublishJobModal
          job={detailsJob}
          onClose={() => setShowUnpublishModal(false)}
          onSuccess={() => {
            setShowUnpublishModal(false);
            setShowDetailsModal(false);
            void reloadHub();
          }}
        />
      )}

      {showPublishModal && detailsJob && (
        <AdminPublishJobModal
          job={detailsJob}
          onClose={() => setShowPublishModal(false)}
          onSuccess={() => {
            setShowPublishModal(false);
            setShowDetailsModal(false);
            void reloadHub();
          }}
        />
      )}

      {showAssignRecruiterModal && detailsJob && (
        <AdminAssignJobToRecruiterModal
          job={detailsJob}
          onClose={() => setShowAssignRecruiterModal(false)}
          onSuccess={() => {
            setShowAssignRecruiterModal(false);
            setShowDetailsModal(false);
            void reloadHub();
          }}
        />
      )}

      {showUpdateStatusModal && detailsJob && (
        <UpdateJobStatusModal
          job={detailsJob}
          onClose={() => setShowUpdateStatusModal(false)}
          onSuccess={() => {
            void refreshJobDetails();
            void reloadHub();
          }}
        />
      )}

      {showShadowRecruiterManager && (
        <ShadowRecruiterManagerModal
          selectedJob={detailsJob}
          onClose={() => {
            setShowShadowRecruiterManager(false);
            void refreshJobDetails();
          }}
        />
      )}
    </div>
  );
}
